/**
 * agent:tool_use / agent:tool_result: the wire the runner's core sink sends
 * (apps/sandbox stream/harness-sink.ts), every field it can carry.
 *
 * Moved out of event-payloads.schema.ts (re-exported there, so every import path is
 * unchanged), which sits at the file-size ratchet. The enrichment fields are typed
 * pass-throughs (`z.custom`): the sink already guards `report` with the full
 * browser / OS report guards and forwards the card fields as the arm built them, so
 * the parse gate checks they exist in the contract, never re-derives their shape.
 */

import { z } from 'zod';
import type { ToolDisplay, ToolResultDisplay } from '@vestra/agent-sdk';
import type { BrowserActionReport } from '../computer-use/action-report.js';
import type { OsActionReport } from '../computer-use/os-action-report.js';
import { SessionIdSchema, ToolUseIdSchema } from './common.schema.js';

export const AgentToolUsePayloadSchema = z.object({
  sessionId: SessionIdSchema,
  toolUseId: ToolUseIdSchema,
  toolName: z.string(),
  toolInput: z.unknown(),
  /** The server-enriched card (Composio / skill) the arm set on the block. Absent otherwise. */
  display: z.custom<ToolDisplay>().optional(),
});

export const AgentToolResultPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  toolUseId: ToolUseIdSchema,
  content: z.unknown(),
  isError: z.boolean().optional(),
  /** TRUE iff this tool_result is a HITL suspension (the round-engine folded
   *  `pausedHalt`). A typed pause outcome — the relay/web read it instead of
   *  inferring a pause from `isError`. Absent for every ordinary result. */
  paused: z.boolean().optional(),
  /** The tool's name, when the round-engine paired the result to its call (telemetry provenance). */
  toolName: z.string().optional(),
  /** The formatted result card (Composio) the arm set on the block. */
  resultDisplay: z.custom<ToolResultDisplay>().optional(),
  /** The actuator's action report, already through the browser / OS report guards at the sink. */
  report: z.custom<BrowserActionReport | OsActionReport>().optional(),
});
